/**
 * The hours booked against one task: what is running, what is recorded, and the
 * three things an operator can do about them.
 *
 * Lifted out of `TaskDetail.tsx` when it stopped being a list and became a
 * screen. It was fifty lines of read-only rows inside a five-hundred-line file
 * that also owns the head, the comments, the status menu and the delete dialog;
 * adding a menu, a dialog and a confirm to it there would have pushed that file
 * past the point where anybody can find anything in it (AGENTS.md section 4).
 * What it needs from the page is the task and a way to say "reload me".
 *
 * WHY THE ACTIONS ARE HERE AT ALL. The project's time tab has offered edit and
 * delete since it was written, and the task page offered neither - so correcting
 * a mistyped hour meant leaving the task, opening its project, finding the tab,
 * finding the row among every other task's, and coming back. Perfex put all
 * three on the task itself, which is where the person who mistyped the hour
 * already is. The routes were already there and had no caller from this side.
 *
 * A RUNNING CLOCK IS NOT AN ENTRY, and the two are separated by more than a line
 * break here. Running clocks are drawn ABOVE the recorded rows, are outside the
 * total in the head, and carry no menu: there is nothing to edit or delete yet,
 * because nothing has been written. The clock's own controls live in the top bar
 * and in the head of this page. Folding a clock's minutes into the figure above
 * would make this card the one screen in the CRM whose hours disagree with the
 * project's, and it would be counting minutes nobody has agreed to.
 *
 * EVERY clock on the task is drawn, not only the reader's own. Two people can be
 * on one task, and a card that hid a colleague's clock would let both of them
 * log the same afternoon.
 *
 * AN INVOICED ENTRY KEEPS ITS TRIGGER and loses its menu items, which is the
 * treatment `TimeEntryRow` settled on for the same fact on the project tab: an
 * action that silently vanishes reads as a broken row rather than a spoken-for
 * one, so the panel carries the sentence instead. The API refuses these anyway;
 * this is so the operator learns why before pressing rather than after.
 */
import { useState } from "react";
import { Clock, Lock, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { deleteTimeEntry, type TimeEntry } from "../../../lib/projects";
import { Card, CardHead, fmtDate, useToast } from "../../ui";
import { Pop, useClosePops } from "../../shell/Pop";
import { useCrmUser } from "../../shell/CrmUser";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { fmtHours } from "../projects/lib";
import { TimeEntryEditModal } from "../projects/TimeEntryEditModal";
import { ElapsedClock, useTimers } from "../../timer";
import { TaskTimeModal } from "./TaskTimeModal";

/** A card body's padding - `crm-card` is a surface and carries none of its own.
 *  The same value `TaskDetail` uses, so the cards in the column line up. */
const BODY = "px-6 pb-5 pt-3";

/**
 * One line of the card: a label on the left, a figure on the right, and an
 * optional control after it.
 *
 * The same row the facts card next door and `ProjectOverview` draw, so a task
 * and a project read the same way. The figure does not break and the label may:
 * a time entry's label is its note, and a note is a sentence - on a 320px phone
 * one of them took four lines and squeezed "3,25 h" into "3,25" over "h".
 */
function Row({
  label,
  value,
  after,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  after?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 border-t border-[var(--crm-hair)] py-2.5 text-[13px] first:border-t-0 first:pt-0">
      <span className="crm-mut min-w-0">{label}</span>
      <span className="crm-push crm-tnum shrink-0 whitespace-nowrap font-semibold">{value}</span>
      {after}
    </div>
  );
}

/**
 * The row's menu, or the reason it has none.
 *
 * The same two items and the same lock note as `TimeEntryRow` on the project
 * tab, in this card's smaller dress. Written here rather than shared with that
 * file because the two rows have nothing else in common: that one is a
 * seven-track grid row and this one is a label and a figure, and a component
 * that served both would take the whole row apart to do it.
 */
function EntryMenu({ locked, onEdit, onDelete }: { locked: boolean; onEdit: () => void; onDelete: () => void }) {
  const t = useT();
  const close = useClosePops();
  return (
    <span className="relative z-[1] shrink-0">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={cn("crm-cb crm-cb-sm", open && "is-on")}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            {locked ? <Lock className="size-3.5" /> : <MoreHorizontal className="size-3.5" />}
          </button>
        )}
      >
        {locked ? (
          // Capped locally: the panel holds one sentence and nothing else, so
          // without a ceiling it lays the whole thing out on one very wide line.
          <p className="crm-menu-note max-w-[248px]">
            <Lock className="size-3.5" />
            {t("projects.time.invoicedLocked")}
          </p>
        ) : (
          <>
            <button
              type="button"
              className="crm-mi"
              onClick={() => {
                close();
                onEdit();
              }}
            >
              <Pencil className="size-4" />
              {t("projects.time.edit")}
            </button>
            <button
              type="button"
              className="crm-mi crm-mi-danger"
              onClick={() => {
                close();
                onDelete();
              }}
            >
              <Trash2 className="size-4" />
              {t("common.delete")}
            </button>
          </>
        )}
      </Pop>
    </span>
  );
}

export function TaskTimesheet({
  taskId,
  taskTitle,
  projectId,
  entries,
  className,
  onChange,
}: {
  taskId: string;
  /** The only option the edit dialog's task picker is given - see below. */
  taskTitle: string;
  projectId: string | null;
  entries: TimeEntry[];
  className?: string;
  /** Reload the task, so its `timeEntries` and the total above them agree with
   *  what was just written. */
  onChange: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const user = useCrmUser();
  const timers = useTimers();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<TimeEntry | null>(null);
  const [deleting, setDeleting] = useState<TimeEntry | null>(null);

  // Only what is recorded. A running clock is deliberately not in here - see the
  // file header.
  const minutes = entries.reduce((sum, e) => sum + e.minutes, 0);

  // From the shell's single list rather than from the task endpoint: the top bar
  // is already holding these, and two reads of the same handful of rows can
  // disagree with each other on one screen.
  const running = timers?.onTask(taskId) ?? [];

  async function remove(entry: TimeEntry) {
    try {
      await deleteTimeEntry(entry.id);
      onChange();
    } catch (err) {
      // The menu offers no delete on an invoiced row, but the API is the guard
      // and it can refuse a row that was invoiced since this page loaded.
      const e = err as { body?: { error?: string } };
      toast(e.body?.error === "entry_invoiced" ? t("projects.time.invoicedLocked") : t("common.error"));
      onChange();
    }
  }

  return (
    <>
      <Card className={className}>
        <CardHead
          title={
            <span className="flex items-center gap-1.5">
              <Clock className="size-3.5" />
              {t("tasks.time")}
            </span>
          }
          /* The total states the sum of what is listed under it, so the head
             claims nothing the rows do not show. Hours rather than
             `fmtDuration`, which formats the seconds off a call recording - a
             timesheet is read in hours.

             The button sits after the figure rather than instead of it: the
             figure is what the card is about, and an operator reaching for
             "log time" is reaching for a control, not for the headline. */
          actions={
            <span className="flex items-center gap-2.5">
              {minutes > 0 ? (
                <span className="crm-tnum text-[13.25px] font-bold">{fmtHours(minutes)} h</span>
              ) : null}
              <button
                type="button"
                className="crm-cb crm-cb-sm"
                onClick={() => setAdding(true)}
                title={t("projects.time.add")}
                aria-label={t("projects.time.add")}
              >
                <Plus className="size-4" />
              </button>
            </span>
          }
        />

        <div className={BODY}>
          {running.map((r) => (
            <Row
              key={r.id}
              label={
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="crm-timer-dot" aria-hidden="true" />
                  <span className="crm-trunc">
                    {r.userId === user.id ? t("timer.runningMine") : t("timer.runningOther")}
                  </span>
                </span>
              }
              value={<ElapsedClock timer={r} />}
            />
          ))}

          {entries.length === 0 ? (
            running.length ? null : <p className="crm-mut text-[13px]">{t("projects.noTime")}</p>
          ) : (
            entries.map((e) => (
              <Row
                key={e.id}
                label={e.note ?? fmtDate(e.startedAt)}
                value={`${fmtHours(e.minutes)} h`}
                after={
                  <EntryMenu
                    locked={!!e.invoicedId}
                    onEdit={() => setEditing(e)}
                    onDelete={() => setDeleting(e)}
                  />
                }
              />
            ))
          )}
        </div>
      </Card>

      {adding ? (
        <TaskTimeModal
          taskId={taskId}
          projectId={projectId}
          onClose={() => setAdding(false)}
          onSaved={onChange}
        />
      ) : null}

      {/* The project tab's dialog, handed ONE task rather than a project's worth
          of them. Its picker is scoped to "this project's tasks" because an
          entry moved to another client's task would be invoiced at that
          project's rate with nothing on screen to show it; a list of one keeps
          that rule and answers the question this page already knows. Moving an
          entry to a DIFFERENT task is a project-tab job, and it is one click
          away in the row's project link. */}
      {editing ? (
        <TimeEntryEditModal
          entry={editing}
          tasks={[{ id: taskId, title: taskTitle }]}
          onClose={() => setEditing(null)}
          onSaved={onChange}
        />
      ) : null}

      {/* Asked rather than done. Deleting recorded hours is not undoable from
          any screen: the minutes leave the project total, leave whatever would
          have been invoiced, and the only surviving record is an audit row
          nobody reads by accident. The figure is in the question, because "3,5 h"
          is what makes an operator notice they have the wrong row. */}
      {deleting ? (
        <ConfirmModal
          title={t("projects.time.deleteTitle")}
          body={t("projects.time.deleteConfirm", { hours: fmtHours(deleting.minutes) })}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={async () => {
            await remove(deleting);
            setDeleting(null);
          }}
          onClose={() => setDeleting(null)}
        />
      ) : null}
    </>
  );
}
